/**
 * Donation Trend Chart Component
 *
 * Shows monthly donation trends over the past 6-12 months
 */

import { FC } from 'react';
import {
  //LineChart,
  //Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  //Legend,
} from 'recharts';

import type { DashboardChartsResponse } from '@/types/dashboard.types';

// Mock data - TODO: Replace with actual API data
const mockData = [
  { period: 'Aug', amount: 1200, count: 4 },
  { period: 'Sep', amount: 980, count: 3 },
  { period: 'Oct', amount: 1450, count: 5 },
  { period: 'Nov', amount: 1100, count: 4 },
  { period: 'Dec', amount: 1850, count: 6 },
  { period: 'Jan', amount: 1250, count: 3 },
];

interface DonationTrendChartProps {
  months: number;
  selectedPeriod: string;
  onPeriodChange: (period: string) => void;
  chartsData: DashboardChartsResponse | null;
}

const getPeriodDescription = (months: number): string => {
  if (months === 6) return 'Last 6 months';
  if (months === 12) return 'Last 12 months';
  return 'Current year';
};

export const DonationTrendChart: FC<DonationTrendChartProps> = ({
  months,
  selectedPeriod,
  onPeriodChange,
  chartsData,
}) => {
  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Donation Trends</h3>
          <p className="text-sm text-gray-600">{getPeriodDescription(months)}</p>
        </div>
        <select
          value={selectedPeriod}
          onChange={(e) => onPeriodChange(e.target.value)}
          className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          <option value="6">Last 6 months</option>
          <option value="12">Last 12 months</option>
          <option value="current-year">Current year</option>
        </select>
      </div>
      <ResponsiveContainer width="100%" height={400}>
        <AreaChart
          data={chartsData?.donation_trends.data_points || mockData}
          margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
        >
          <defs>
            <linearGradient id="colorAmount" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#7c3aed" stopOpacity={0.3} />
              <stop offset="95%" stopColor="#7c3aed" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis dataKey="period" stroke="#9ca3af" fontSize={12} />
          <YAxis stroke="#9ca3af" fontSize={12} />
          <Tooltip
            formatter={(value: number | undefined) =>
              value !== undefined
                ? `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                : '$0.00'
            }
            contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              padding: '12px',
            }}
          />
          <Area
            type="monotone"
            dataKey="amount"
            stroke="#7c3aed"
            strokeWidth={2}
            fillOpacity={1}
            fill="url(#colorAmount)"
          />
        </AreaChart>
      </ResponsiveContainer>
      <div className="mt-4 grid grid-cols-3 gap-4 text-center border-t pt-4">
        <div>
          <p className="text-sm text-gray-600">Average</p>
          <p className="text-lg font-semibold text-gray-900">
            $
            {chartsData?.donation_trends.average_amount.toLocaleString('en-US', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </p>
        </div>
        <div>
          <p className="text-sm text-gray-600">Highest</p>
          <p className="text-lg font-semibold text-green-600">
            $
            {chartsData?.donation_trends.max_amount.toLocaleString('en-US', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </p>
        </div>
        <div>
          <p className="text-sm text-gray-600">Total</p>
          <p className="text-lg font-semibold text-primary-600">
            $
            {chartsData?.donation_trends.total_amount.toLocaleString('en-US', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </p>
        </div>
      </div>
    </div>
  );
};
